import React, { useState, memo, useCallback } from "react";
import {
  Typography,
  Button,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Chip,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Alert,
  Avatar,
  Collapse,
  Divider,
  InputAdornment,
  ListItemIcon,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add,
  MoreHoriz,
  Edit,
  Delete,
  Search,
  Close,
  ChevronRight,
  KeyboardArrowDown,
  KeyboardArrowRight,
  DashboardOutlined,
  FolderOpenOutlined,
  CreateNewFolderOutlined,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/EnterpriseAuthContext";
import { formatDate } from "../utils/dateUtils";
import {
  PageContainer,
  PageHeader,
  SectionCard,
  EmptyState,
  ListSkeleton,
  ConfirmDialog,
} from "./pagekit";

/** Maximum allowed length for a customer name. */
const CUSTOMER_NAME_MAX_LENGTH = 120;
/** Reasonable email shape: no spaces, one @, and a dot in the domain. */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
/** Phone: digits/spaces and + - ( ) / . only, with at least one digit. */
const PHONE_REGEX = /^(?=.*\d)[\d\s+\-()./]+$/;
/** Bare domain shape, e.g. "example.com" (labels separated by dots). */
const DOMAIN_REGEX = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)+$/;

/**
 * Validate an email value.
 * @param {string} value - Raw input.
 * @returns {string} Error message, or "" when valid/empty.
 */
const getEmailError = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return EMAIL_REGEX.test(trimmed) ? "" : "Enter a valid email address";
};

/**
 * Validate a phone value.
 * @param {string} value - Raw input.
 * @returns {string} Error message, or "" when valid/empty.
 */
const getPhoneError = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return PHONE_REGEX.test(trimmed)
    ? ""
    : "Phone may only contain digits, spaces, and + - ( ) / .";
};

/**
 * Validate a website value. Only http(s) URLs are allowed; bare domains
 * are accepted (treated as https). Other schemes (e.g. javascript:) are rejected.
 * @param {string} value - Raw input.
 * @returns {string} Error message, or "" when valid/empty.
 */
const getWebsiteError = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (!/^https?:\/\//i.test(trimmed) && /^[a-z][a-z0-9+.-]*:/i.test(trimmed)) {
    return "Website must use http:// or https://";
  }
  const host = trimmed
    .replace(/^https?:\/\//i, "")
    .replace(/^www\./i, "")
    .split(/[/?#]/)[0];
  return DOMAIN_REGEX.test(host.toLowerCase())
    ? ""
    : "Enter a valid website (e.g. https://example.com)";
};

/**
 * Normalize a valid website value for saving: prefix bare domains with https://.
 * @param {string} value - Raw input.
 * @returns {string} Normalized website URL or "".
 */
const normalizeWebsite = (value) => {
  const trimmed = value.trim();
  if (!trimmed) return "";
  return /^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
};

/**
 * Normalize a domain string by stripping protocol, www prefix, and trailing slashes.
 * @param {string} raw - Raw domain input.
 * @returns {string} Cleaned domain (e.g. "example.com").
 */
const normalizeDomain = (raw) => {
  let d = raw.trim().toLowerCase();
  d = d.replace(/^https?:\/\//, "");
  d = d.replace(/^www\./, "");
  d = d.replace(/\/+$/, "");
  return d;
};

/** Blank customer form. */
const EMPTY_CUSTOMER_FORM = {
  name: "",
  description: "",
  contact_email: "",
  contact_phone: "",
  company_website: "",
  address: "",
  domain: [],
};

/**
 * Customer fields shared by the create and edit dialogs.
 * @param {object} props
 * @param {object} props.formData - Customer form state.
 * @param {Function} props.setFormData - Form state setter.
 * @param {{contact_email: string, contact_phone: string, company_website: string}} props.fieldErrors - Inline errors.
 * @param {string} props.domainInput - Pending domain text.
 * @param {Function} props.setDomainInput - Setter for the pending domain.
 * @param {string} props.domainError - Domain error.
 * @param {Function} props.setDomainError - Setter for the domain error.
 * @param {Function} props.onAddDomain - Commit the pending domain.
 * @param {Function} props.onRemoveDomain - (domain) => void.
 * @returns {JSX.Element}
 */
export function CustomerFormFields({
  formData,
  setFormData,
  fieldErrors,
  domainInput,
  setDomainInput,
  domainError,
  setDomainError,
  onAddDomain,
  onRemoveDomain,
}) {
  const set = (key) => (e) => setFormData({ ...formData, [key]: e.target.value });
  return (
    <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
      <TextField
        autoFocus
        label="Customer name"
        fullWidth
        value={formData.name}
        onChange={set("name")}
        inputProps={{ maxLength: CUSTOMER_NAME_MAX_LENGTH }}
        helperText={
          formData.name.length >= CUSTOMER_NAME_MAX_LENGTH - 20
            ? `${formData.name.length}/${CUSTOMER_NAME_MAX_LENGTH} characters`
            : undefined
        }
      />
      <TextField label="Description" fullWidth multiline minRows={2} value={formData.description} onChange={set("description")} />
      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" } }}>
        <TextField
          label="Contact email"
          type="email"
          fullWidth
          value={formData.contact_email}
          onChange={set("contact_email")}
          error={Boolean(fieldErrors.contact_email)}
          helperText={fieldErrors.contact_email || undefined}
        />
        <TextField
          label="Contact phone"
          fullWidth
          value={formData.contact_phone}
          onChange={set("contact_phone")}
          error={Boolean(fieldErrors.contact_phone)}
          helperText={fieldErrors.contact_phone || undefined}
        />
      </Box>
      <TextField
        label="Company website"
        type="url"
        fullWidth
        value={formData.company_website}
        onChange={set("company_website")}
        error={Boolean(fieldErrors.company_website)}
        helperText={fieldErrors.company_website || undefined}
      />
      <TextField label="Address" fullWidth multiline minRows={2} value={formData.address} onChange={set("address")} />
      <Box>
        <TextField
          label="Email domains"
          fullWidth
          value={domainInput}
          onChange={(e) => {
            setDomainInput(e.target.value);
            if (domainError) setDomainError("");
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              onAddDomain();
            }
          }}
          onBlur={onAddDomain}
          placeholder="e.g. example.com"
          error={Boolean(domainError)}
          helperText={domainError || "Press Enter to add. Meetings with attendees from these domains are matched to this customer."}
        />
        {formData.domain.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
            {formData.domain.map((d) => (
              <Chip key={d} label={d} size="small" onDelete={() => onRemoveDomain(d)} color="info" />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
}

/**
 * Customers page: customers with their projects, search, and create / edit /
 * delete for both.
 * @returns {JSX.Element}
 */
const CustomerList = memo(() => {
  const navigate = useNavigate();
  const { state, actions } = useApp();
  const { state: authState } = useAuth();
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down("sm"));
  const [search, setSearch] = useState("");
  const [expandedOverrides, setExpandedOverrides] = useState({});
  const [pendingDelete, setPendingDelete] = useState(null); // { type: 'customer'|'project', item }
  const [createCustomerDialogOpen, setCreateCustomerDialogOpen] =
    useState(false);
  const [createProjectDialogOpen, setCreateProjectDialogOpen] = useState(false);
  const [editCustomerDialogOpen, setEditCustomerDialogOpen] = useState(false);
  const [customerToEdit, setCustomerToEdit] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [menuType, setMenuType] = useState(""); // 'customer' or 'project'
  const [editProjectDialogOpen, setEditProjectDialogOpen] = useState(false);
  const [projectToEdit, setProjectToEdit] = useState(null);
  const [editProjectFormData, setEditProjectFormData] = useState({
    name: "",
    description: "",
    customer_id: "",
  });
  const [customerFormData, setCustomerFormData] = useState({
    name: "",
    description: "",
    contact_email: "",
    contact_phone: "",
    company_website: "",
    address: "",
    domain: [],
  });
  const [domainInput, setDomainInput] = useState("");
  const [domainError, setDomainError] = useState("");
  const [customerCreatedSnackbarOpen, setCustomerCreatedSnackbarOpen] =
    useState(false);
  const [createCustomerError, setCreateCustomerError] = useState("");
  const [projectFormData, setProjectFormData] = useState({
    name: "",
    description: "",
  });

  /** Inline validation errors for the customer contact fields. */
  const customerFieldErrors = React.useMemo(
    () => ({
      contact_email: getEmailError(customerFormData.contact_email),
      contact_phone: getPhoneError(customerFormData.contact_phone),
      company_website: getWebsiteError(customerFormData.company_website),
    }),
    [
      customerFormData.contact_email,
      customerFormData.contact_phone,
      customerFormData.company_website,
    ]
  );
  const hasCustomerFieldErrors = Object.values(customerFieldErrors).some(
    Boolean
  );

  const handleCreateCustomer = useCallback(async () => {
    if (customerFormData.name.trim() && !hasCustomerFieldErrors) {
      // Commit any valid pending domain text so it isn't silently lost
      let domains = customerFormData.domain;
      if (domainInput.trim()) {
        const cleaned = normalizeDomain(domainInput);
        if (!DOMAIN_REGEX.test(cleaned)) {
          setDomainError("Enter a valid domain (e.g. example.com)");
          return;
        }
        if (!domains.includes(cleaned)) {
          domains = [...domains, cleaned];
        }
      }
      setCreateCustomerError("");
      const result = await actions.createCustomer({
        ...customerFormData,
        company_website: normalizeWebsite(customerFormData.company_website),
        domain: domains,
      });
      if (!result?.success) {
        // Keep the dialog open so the user's input isn't lost.
        setCreateCustomerError(
          result?.error || "Failed to create customer. Please try again."
        );
        return;
      }
      setCustomerFormData({
        name: "",
        description: "",
        contact_email: "",
        contact_phone: "",
        company_website: "",
        address: "",
        domain: [],
      });
      setDomainInput("");
      setDomainError("");
      setCreateCustomerDialogOpen(false);
      setCustomerCreatedSnackbarOpen(true);
    }
  }, [actions, customerFormData, hasCustomerFieldErrors, domainInput]);

  const handleCreateProject = useCallback(() => {
    if (projectFormData.name.trim() && selectedCustomer) {
      actions.createProject({
        ...projectFormData,
        customer_id: selectedCustomer.id,
      });
      setProjectFormData({ name: "", description: "" });
      setCreateProjectDialogOpen(false);
      setSelectedCustomer(null);
    }
  }, [actions, projectFormData, selectedCustomer]);

  const handleEditCustomer = useCallback(() => {
    if (customerFormData.name.trim() && customerToEdit && !hasCustomerFieldErrors) {
      // Commit any valid pending domain text so it isn't silently lost
      let domains = customerFormData.domain;
      if (domainInput.trim()) {
        const cleaned = normalizeDomain(domainInput);
        if (!DOMAIN_REGEX.test(cleaned)) {
          setDomainError("Enter a valid domain (e.g. example.com)");
          return;
        }
        if (!domains.includes(cleaned)) {
          domains = [...domains, cleaned];
        }
      }
      actions.updateCustomer(customerToEdit.id, {
        ...customerFormData,
        company_website: normalizeWebsite(customerFormData.company_website),
        domain: domains,
      });
      setCustomerFormData({
        name: "",
        description: "",
        contact_email: "",
        contact_phone: "",
        company_website: "",
        address: "",
        domain: [],
      });
      setDomainInput("");
      setDomainError("");
      setEditCustomerDialogOpen(false);
      setCustomerToEdit(null);
    }
  }, [
    actions,
    customerFormData,
    customerToEdit,
    hasCustomerFieldErrors,
    domainInput,
  ]);

  const handleDeleteCustomer = useCallback(
    (customerId) => {
      actions.deleteCustomer(customerId);
      setMenuAnchor(null);
      setSelectedItem(null);
    },
    [actions]
  );

  const handleDeleteProject = useCallback(
    (projectId) => {
      actions.deleteProject(projectId);
      setMenuAnchor(null);
      setSelectedItem(null);
    },
    [actions]
  );

  const openEditProjectDialog = useCallback((project) => {
    setProjectToEdit(project);
    setEditProjectFormData({
      name: project.name,
      description: project.description || "",
      customer_id: project.customer_id,
    });
    setEditProjectDialogOpen(true);
    setMenuAnchor(null);
    setSelectedItem(null);
  }, []);

  const handleEditProject = useCallback(() => {
    if (
      editProjectFormData.name.trim() &&
      editProjectFormData.customer_id &&
      projectToEdit
    ) {
      actions.updateProject(projectToEdit.id, {
        name: editProjectFormData.name,
        description: editProjectFormData.description,
        customer_id: editProjectFormData.customer_id,
      });
      setEditProjectFormData({
        name: "",
        description: "",
        customer_id: "",
      });
      setEditProjectDialogOpen(false);
      setProjectToEdit(null);
    }
  }, [actions, editProjectFormData, projectToEdit]);

  const openEditCustomerDialog = useCallback((customer) => {
    setCustomerToEdit(customer);
    setCustomerFormData({
      name: customer.name,
      description: customer.description || "",
      contact_email: customer.contact_email || "",
      contact_phone: customer.contact_phone || "",
      company_website: customer.company_website || "",
      address: customer.address || "",
      domain: customer.domain || [],
    });
    setDomainInput("");
    setDomainError("");
    setEditCustomerDialogOpen(true);
    setMenuAnchor(null);
    setSelectedItem(null);
  }, []);

  /**
   * Add a domain chip from the current domainInput value.
   * Shows an inline error when the input is not a valid domain.
   */
  const handleAddDomain = useCallback(() => {
    if (!domainInput.trim()) return;
    const cleaned = normalizeDomain(domainInput);
    if (!DOMAIN_REGEX.test(cleaned)) {
      setDomainError("Enter a valid domain (e.g. example.com)");
      return;
    }
    setDomainError("");
    if (customerFormData.domain.includes(cleaned)) {
      setDomainInput("");
      return;
    }
    setCustomerFormData((prev) => ({
      ...prev,
      domain: [...prev.domain, cleaned],
    }));
    setDomainInput("");
  }, [domainInput, customerFormData.domain]);

  /**
   * Remove a domain chip by value.
   * @param {string} domainToRemove - The domain to remove.
   */
  const handleRemoveDomain = useCallback((domainToRemove) => {
    setCustomerFormData((prev) => ({
      ...prev,
      domain: prev.domain.filter((d) => d !== domainToRemove),
    }));
  }, []);

  const openAddProjectDialog = useCallback((customer) => {
    setSelectedCustomer(customer);
    setCreateProjectDialogOpen(true);
    setMenuAnchor(null);
    setSelectedItem(null);
  }, []);

  const handleMenuOpen = useCallback((event, item, type) => {
    setMenuAnchor(event.currentTarget);
    setSelectedItem(item);
    setMenuType(type);
  }, []);

  const handleMenuClose = useCallback(() => {
    setMenuAnchor(null);
    setSelectedItem(null);
    setMenuType("");
  }, []);

  // Group projects by customer
  const customerProjects = React.useMemo(() => {
    const grouped = {};

    // Initialize all customers
    state.customers?.forEach((customer) => {
      grouped[customer.id] = {
        customer,
        projects: [],
      };
    });

    // Group projects by customer
    state.projects?.forEach((project) => {
      const customerId = project.customer_id;
      if (grouped[customerId]) {
        grouped[customerId].projects.push(project);
      }
    });

    return Object.values(grouped);
  }, [state.customers, state.projects]);

  const canDeleteCustomer = useCallback(
    (customer) => {
      // Can't delete "No Customer" or customers with projects
      if (customer.name === "No Customer") return false;
      const customerGroup = customerProjects.find(
        (cp) => cp.customer.id === customer.id
      );
      return !customerGroup?.projects.length;
    },
    [customerProjects]
  );

  const openCreateCustomer = () => {
    setCustomerFormData(EMPTY_CUSTOMER_FORM);
    setDomainInput("");
    setDomainError("");
    setCreateCustomerError("");
    setCreateCustomerDialogOpen(true);
  };

  const filteredGroups = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return customerProjects;
    return customerProjects
      .map(({ customer, projects }) => {
        const customerMatch =
          customer.name?.toLowerCase().includes(q) ||
          customer.description?.toLowerCase().includes(q) ||
          (customer.domain || []).some((d) => d.toLowerCase().includes(q));
        const matchingProjects = projects.filter(
          (p) => p.name?.toLowerCase().includes(q) || p.description?.toLowerCase().includes(q)
        );
        if (customerMatch) return { customer, projects };
        if (matchingProjects.length) return { customer, projects: matchingProjects };
        return null;
      })
      .filter(Boolean);
  }, [customerProjects, search]);

  const isExpanded = (customerId) =>
    search.trim() ? true : expandedOverrides[customerId] ?? customerProjects.length <= 6;

  const toggleExpanded = (customerId) =>
    setExpandedOverrides((prev) => ({ ...prev, [customerId]: !isExpanded(customerId) }));

  const loading = state.loading || (!state.initialLoadComplete && authState.loading);

  let body;
  if (loading) {
    body = (
      <SectionCard flush>
        <ListSkeleton rows={4} label="Loading customers" />
      </SectionCard>
    );
  } else if (!state.initialLoadComplete) {
    body = null;
  } else if (!customerProjects.length) {
    body = (
      <SectionCard>
        <EmptyState
          inky
          title="No customers yet"
          description="Customers group your projects. Add attendee email domains and imported meetings are matched to the right customer automatically."
          actions={
            <Button variant="contained" startIcon={<Add />} onClick={openCreateCustomer}>
              New customer
            </Button>
          }
        />
      </SectionCard>
    );
  } else if (!filteredGroups.length) {
    body = (
      <SectionCard>
        <EmptyState
          compact
          icon={<Search />}
          title="No customers or projects match"
          description="Try a different search."
          actions={<Button variant="outlined" onClick={() => setSearch("")}>Clear search</Button>}
        />
      </SectionCard>
    );
  } else {
    body = filteredGroups.map(({ customer, projects }) => {
      const isPlaceholder = customer.name === "No Customer";
      const expanded = isExpanded(customer.id);
      const domains = customer.domain || [];
      return (
        <SectionCard key={customer.id} flush component="section" aria-label={customer.name}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 1.5,
              px: { xs: 1.5, sm: 2 },
              py: 1.5,
              borderBottom: expanded ? 1 : 0,
              borderColor: "var(--q-border-subtle)",
            }}
          >
            <IconButton
              size="small"
              onClick={() => toggleExpanded(customer.id)}
              aria-expanded={expanded}
              aria-label={expanded ? `Hide projects of ${customer.name}` : `Show projects of ${customer.name}`}
            >
              {expanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
            </IconButton>
            <Avatar
              variant="rounded"
              sx={{ width: 36, height: 36, bgcolor: "var(--q-accent-soft)", color: "var(--q-accent-text)", fontWeight: 600, fontSize: 15 }}
            >
              {(customer.name || "?").trim().charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ flex: "1 1 200px", minWidth: 0 }}>
              <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600 }} noWrap title={customer.name}>
                {customer.name}
              </Typography>
              <Typography variant="caption" color="text.secondary" component="div" noWrap>
                {[
                  `${projects.length} project${projects.length !== 1 ? "s" : ""}`,
                  domains.length ? domains.slice(0, 2).join(", ") + (domains.length > 2 ? ` +${domains.length - 2}` : "") : null,
                  customer.description,
                ].filter(Boolean).join(" · ")}
              </Typography>
            </Box>
            {!isPlaceholder && (
              <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
                <Button size="small" startIcon={<Add />} onClick={() => openAddProjectDialog(customer)}>
                  Project
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<DashboardOutlined />}
                  onClick={() => navigate(`/customers/${customer.id}`)}
                >
                  Dashboard
                </Button>
                <IconButton
                  size="small"
                  aria-label={`Actions for ${customer.name}`}
                  onClick={(e) => handleMenuOpen(e, customer, "customer")}
                >
                  <MoreHoriz fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>

          <Collapse in={expanded} unmountOnExit>
            {!projects.length ? (
              <EmptyState
                compact
                icon={<FolderOpenOutlined />}
                title={`No projects for ${customer.name}`}
                description="Create a project to start capturing meetings and requirements."
                actions={!isPlaceholder ? (
                  <Button variant="outlined" startIcon={<Add />} onClick={() => openAddProjectDialog(customer)}>
                    New project
                  </Button>
                ) : null}
              />
            ) : (
              <Box component="ul" sx={{ m: 0, p: 0 }}>
                {projects.map((project) => (
                  <Box
                    component="li"
                    key={project.id}
                    sx={{
                      listStyle: "none",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      pr: { xs: 1, sm: 1.5 },
                      borderBottom: 1,
                      borderColor: "var(--q-row-divider)",
                      "&:last-of-type": { borderBottom: 0 },
                      "&:hover": { bgcolor: "var(--q-bg-subtle)" },
                    }}
                  >
                    <Box
                      component="button"
                      type="button"
                      onClick={() => navigate(`/projects/${project.id}`)}
                      sx={{
                        all: "unset",
                        boxSizing: "border-box",
                        cursor: "pointer",
                        flex: 1,
                        minWidth: 0,
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        pl: { xs: 1.5, sm: 7.5 },
                        py: 1.25,
                        "&:focus-visible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: -2 },
                      }}
                    >
                      <FolderOpenOutlined fontSize="small" sx={{ color: "text.secondary", flex: "none" }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                          {project.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" component="div" noWrap>
                          {project.description || `Created ${formatDate(project.createdAt)}`}
                        </Typography>
                      </Box>
                      <Box sx={{ display: { xs: "none", sm: "flex" }, gap: 0.75 }}>
                        <Chip size="small" label={`${project.sessions?.length || 0} sessions`} />
                        <Chip size="small" label={`${project.requirements?.length || 0} requirements`} />
                        <Chip size="small" label={`${project.generatedDocuments?.length || 0} documents`} />
                      </Box>
                      <ChevronRight fontSize="small" sx={{ color: "text.secondary" }} />
                    </Box>
                    <IconButton
                      size="small"
                      aria-label={`Actions for ${project.name}`}
                      onClick={(e) => handleMenuOpen(e, project, "project")}
                    >
                      <MoreHoriz fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
          </Collapse>
        </SectionCard>
      );
    });
  }

  const customerFormProps = {
    formData: customerFormData,
    setFormData: setCustomerFormData,
    fieldErrors: customerFieldErrors,
    domainInput,
    setDomainInput,
    domainError,
    setDomainError,
    onAddDomain: handleAddDomain,
    onRemoveDomain: handleRemoveDomain,
  };

  return (
    <PageContainer>
      <PageHeader
        title="Customers"
        description="Customers group projects. Their email domains route imported meetings to the right place."
        actions={
          <Button variant="contained" startIcon={<Add />} onClick={openCreateCustomer}>
            New customer
          </Button>
        }
      />

      {customerProjects.length > 0 && !loading && (
        <TextField
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search customers, domains or projects"
          inputProps={{ "aria-label": "Search customers" }}
          sx={{ maxWidth: 480, "& .MuiOutlinedInput-root": { bgcolor: "background.paper" } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton size="small" aria-label="Clear search" onClick={() => setSearch("")}>
                  <Close fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
      )}

      {body}

      {/* Create Customer Dialog */}
      <Dialog
        open={createCustomerDialogOpen}
        onClose={() => setCreateCustomerDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isPhone}
        disableRestoreFocus
      >
        <DialogTitle>New customer</DialogTitle>
        <DialogContent>
          {createCustomerError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {createCustomerError}
            </Alert>
          )}
          <CustomerFormFields {...customerFormProps} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateCustomerDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreateCustomer}
            variant="contained"
            disabled={!customerFormData.name.trim() || hasCustomerFieldErrors}
          >
            Create customer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Project Dialog */}
      <Dialog
        open={createProjectDialogOpen}
        onClose={() => setCreateProjectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle>New project for {selectedCustomer?.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
            <TextField
              autoFocus
              label="Project name"
              fullWidth
              value={projectFormData.name}
              onChange={(e) => setProjectFormData({ ...projectFormData, name: e.target.value })}
            />
            <TextField
              label="Description (optional)"
              fullWidth
              multiline
              minRows={3}
              value={projectFormData.description}
              onChange={(e) => setProjectFormData({ ...projectFormData, description: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setCreateProjectDialogOpen(false);
              setSelectedCustomer(null);
            }}
          >
            Cancel
          </Button>
          <Button onClick={handleCreateProject} variant="contained" disabled={!projectFormData.name.trim()}>
            Create project
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Customer Dialog */}
      <Dialog
        open={editCustomerDialogOpen}
        onClose={() => setEditCustomerDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isPhone}
        disableRestoreFocus
      >
        <DialogTitle>Edit customer</DialogTitle>
        <DialogContent>
          <CustomerFormFields {...customerFormProps} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditCustomerDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleEditCustomer}
            variant="contained"
            disabled={!customerFormData.name.trim() || hasCustomerFieldErrors}
          >
            Save changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog
        open={editProjectDialogOpen}
        onClose={() => setEditProjectDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        disableRestoreFocus
      >
        <DialogTitle>Edit project</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="edit-project-customer">Customer</InputLabel>
              <Select
                labelId="edit-project-customer"
                value={editProjectFormData.customer_id}
                onChange={(e) => setEditProjectFormData({ ...editProjectFormData, customer_id: e.target.value })}
                label="Customer"
              >
                {state.customers?.map((customer) => (
                  <MenuItem key={customer.id} value={customer.id}>
                    {customer.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              autoFocus
              label="Project name"
              fullWidth
              value={editProjectFormData.name}
              onChange={(e) => setEditProjectFormData({ ...editProjectFormData, name: e.target.value })}
            />
            <TextField
              label="Description (optional)"
              fullWidth
              multiline
              minRows={3}
              value={editProjectFormData.description}
              onChange={(e) => setEditProjectFormData({ ...editProjectFormData, description: e.target.value })}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setEditProjectDialogOpen(false);
              setProjectToEdit(null);
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleEditProject}
            variant="contained"
            disabled={!editProjectFormData.name.trim() || !editProjectFormData.customer_id}
          >
            Save changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {menuType === "customer" &&
          selectedItem && [
            <MenuItem key="edit" onClick={() => openEditCustomerDialog(selectedItem)}>
              <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
              Edit customer
            </MenuItem>,
            ...(selectedItem.name !== "No Customer"
              ? [
                  <MenuItem key="add-project" onClick={() => openAddProjectDialog(selectedItem)}>
                    <ListItemIcon><CreateNewFolderOutlined fontSize="small" /></ListItemIcon>
                    Add project
                  </MenuItem>,
                ]
              : []),
            <Divider key="divider" />,
            canDeleteCustomer(selectedItem) ? (
              <MenuItem
                key="delete"
                onClick={() => {
                  const item = selectedItem;
                  handleMenuClose();
                  setPendingDelete({ type: "customer", item });
                }}
                sx={{ color: "error.main" }}
              >
                <ListItemIcon sx={{ color: "inherit" }}><Delete fontSize="small" /></ListItemIcon>
                Delete customer
              </MenuItem>
            ) : (
              <MenuItem key="delete-disabled" disabled>
                <ListItemIcon><Delete fontSize="small" /></ListItemIcon>
                Delete (remove its projects first)
              </MenuItem>
            ),
          ]}
        {menuType === "project" &&
          selectedItem && [
            <MenuItem
              key="open"
              onClick={() => {
                const item = selectedItem;
                handleMenuClose();
                navigate(`/projects/${item.id}`);
              }}
            >
              <ListItemIcon><FolderOpenOutlined fontSize="small" /></ListItemIcon>
              Open project
            </MenuItem>,
            <MenuItem key="edit" onClick={() => openEditProjectDialog(selectedItem)}>
              <ListItemIcon><Edit fontSize="small" /></ListItemIcon>
              Edit project
            </MenuItem>,
            <Divider key="divider" />,
            <MenuItem
              key="delete"
              onClick={() => {
                const item = selectedItem;
                handleMenuClose();
                setPendingDelete({ type: "project", item });
              }}
              sx={{ color: "error.main" }}
            >
              <ListItemIcon sx={{ color: "inherit" }}><Delete fontSize="small" /></ListItemIcon>
              Delete project
            </MenuItem>,
          ]}
      </Menu>

      <ConfirmDialog
        open={Boolean(pendingDelete)}
        title={pendingDelete?.type === "customer" ? "Delete customer?" : "Delete project?"}
        message={`"${pendingDelete?.item?.name || ""}" will be deleted for everyone in your workspace. This cannot be undone.`}
        confirmLabel={pendingDelete?.type === "customer" ? "Delete customer" : "Delete project"}
        onConfirm={() => {
          if (pendingDelete?.type === "customer") handleDeleteCustomer(pendingDelete.item.id);
          else if (pendingDelete?.type === "project") handleDeleteProject(pendingDelete.item.id);
          setPendingDelete(null);
        }}
        onClose={() => setPendingDelete(null)}
      />

      {/* Customer Created Snackbar */}
      <Snackbar
        open={customerCreatedSnackbarOpen}
        autoHideDuration={4000}
        onClose={() => setCustomerCreatedSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={() => setCustomerCreatedSnackbarOpen(false)} severity="success" sx={{ width: "100%" }}>
          Customer created
        </Alert>
      </Snackbar>
    </PageContainer>
  );
});

CustomerList.displayName = "CustomerList";

export default CustomerList;
